import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import { Textarea } from '@/components/ui/textarea.js';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select.js';
import { Loader2, Upload, CheckCircle2, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext.js';
import { getApiUrl } from '@/lib/api-url.js';
import { registerService } from '@/services/registryService.js';

interface PublishAgentDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

type PublishStatus = 'idle' | 'loading' | 'success' | 'error';

export function PublishAgentDialog({ open, onOpenChange }: PublishAgentDialogProps) {
    const { user } = useAuth();
    const [status, setStatus] = useState<PublishStatus>('idle');
    const [error, setError] = useState('');
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Form fields
    const [agentUrl, setAgentUrl] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [bio, setBio] = useState('');
    const [visibility, setVisibility] = useState<'public' | 'private'>('public');
    const [tags, setTags] = useState('');

    // Auto-fetch agent card on open
    useEffect(() => {
        if (open && user) {
            fetchAgentInfo();
        }
    }, [open, user]);

    const fetchAgentInfo = async () => {
        const baseUrl = getApiUrl();

        // NEW: Use user-scoped URL format for multi-tenant addressing
        // This ensures each user has a unique, publicly addressable endpoint
        const userScopedUrl = `${baseUrl}/u/${user!.id}`;
        setAgentUrl(userScopedUrl);

        // Always use the user's actual name
        const userName = user!.user_metadata?.full_name ||
                        user!.user_metadata?.name ||
                        user!.email?.split('@')[0] ||
                        'User';

        // Simple defaults
        setDisplayName(userName);
        setBio(`${userName}'s AI assistant`);
    };

    const handlePublish = async () => {
        if (!displayName || !bio) {
            setError('Please enter your name and bio');
            return;
        }

        if (!agentUrl) {
            setError('Assistant endpoint URL is required');
            return;
        }

        if (!user) {
            setError('You must be logged in to publish');
            return;
        }

        setStatus('loading');
        setError('');

        try {
            // Use the user's unique ID and info for registration
            const userId = user.id;
            const username = user.email?.split('@')[0] || 'User';

            // Parse tags
            const tagList = tags.split(',').map(t => t.trim()).filter(Boolean);

            // Register the user's personal agent to the network
            // This publishes their unique assistant instance with user-scoped URL
            const requestData = {
                url: agentUrl.trim(), // This is now the user-scoped URL: baseUrl/u/:userId
                owner: {
                    userId,
                    username,
                    email: user.email || undefined,
                },
                visibility: visibility as 'public' | 'private',
                manual: {
                    name: displayName.trim(),
                    description: bio.trim(),
                    discoveryType: 'a2a' as const,
                    tags: tagList.length > 0 ? tagList : undefined,
                },
            };

            console.log('[PublishAgent] Sending registration:', requestData);

            await registerService(requestData);

            setStatus('success');

            // Auto-close after success
            setTimeout(() => {
                onOpenChange(false);
                resetForm();
            }, 2000);
        } catch (err) {
            console.error('[PublishAgent] Registration error:', err);
            setStatus('error');

            // Try to extract a more specific error message
            const errorMessage = err instanceof Error ? err.message : 'Failed to publish agent';
            setError(errorMessage);
        }
    };

    const resetForm = () => {
        setStatus('idle');
        setError('');
        setDisplayName('');
        setBio('');
        setTags('');
        setVisibility('public');
        setShowAdvanced(false);
    };

    const handleClose = () => {
        onOpenChange(false);
        if (status !== 'loading') {
            resetForm();
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[550px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Upload className="h-5 w-5" />
                        Publish Your Assistant
                    </DialogTitle>
                    <DialogDescription>
                        Create your assistant's public profile on the Dexto Network
                    </DialogDescription>
                </DialogHeader>

                {status === 'success' ? (
                    <div className="flex flex-col items-center justify-center py-8">
                        <CheckCircle2 className="h-16 w-16 text-green-600 mb-4" />
                        <h3 className="text-lg font-semibold mb-2">Successfully Published!</h3>
                        <p className="text-sm text-muted-foreground text-center">
                            Your personal assistant is now discoverable on the Dexto Network.<br />
                            Others can find and add it to their contacts.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Display Name */}
                        <div className="space-y-2">
                            <Label htmlFor="displayName">
                                Name <span className="text-destructive">*</span>
                            </Label>
                            <Input
                                id="displayName"
                                placeholder="John"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                disabled={status === 'loading'}
                            />
                            <p className="text-xs text-muted-foreground">
                                Your name or how others should refer to you
                            </p>
                        </div>

                        {/* Bio */}
                        <div className="space-y-2">
                            <Label htmlFor="bio">
                                Description <span className="text-destructive">*</span>
                            </Label>
                            <Textarea
                                id="bio"
                                placeholder="John's AI assistant"
                                value={bio}
                                onChange={(e) => setBio(e.target.value)}
                                disabled={status === 'loading'}
                                rows={3}
                                maxLength={300}
                            />
                            <p className="text-xs text-muted-foreground text-right">
                                {bio.length}/300 characters
                            </p>
                        </div>

                        {/* Visibility */}
                        <div className="space-y-2">
                            <Label htmlFor="visibility">Visibility</Label>
                            <Select
                                value={visibility}
                                onValueChange={(value: 'public' | 'private') => setVisibility(value)}
                                disabled={status === 'loading'}
                            >
                                <SelectTrigger id="visibility">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="public">Public</SelectItem>
                                    <SelectItem value="private">Private</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                                {visibility === 'public'
                                    ? 'Anyone can discover and add your assistant'
                                    : 'Only you can see your assistant profile'}
                            </p>
                        </div>

                        {/* Advanced Section */}
                        <div className="border-t pt-4">
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowAdvanced(!showAdvanced)}
                                className="w-full justify-between"
                            >
                                <span className="text-sm font-medium">Advanced Settings</span>
                                {showAdvanced ? (
                                    <ChevronUp className="h-4 w-4" />
                                ) : (
                                    <ChevronDown className="h-4 w-4" />
                                )}
                            </Button>

                            {showAdvanced && (
                                <div className="mt-4 space-y-4">
                                    {/* Agent URL (editable) */}
                                    <div className="space-y-2">
                                        <Label htmlFor="agentUrl" className="text-sm text-muted-foreground">
                                            Assistant Endpoint
                                        </Label>
                                        <Input
                                            id="agentUrl"
                                            value={agentUrl}
                                            onChange={(e) => setAgentUrl(e.target.value)}
                                            disabled={status === 'loading'}
                                            className="font-mono text-xs"
                                            placeholder="https://your-domain.com"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            The URL where your assistant can be reached
                                        </p>
                                    </div>

                                    {/* Tags */}
                                    <div className="space-y-2">
                                        <Label htmlFor="tags" className="text-sm text-muted-foreground">
                                            Tags (optional)
                                        </Label>
                                        <Input
                                            id="tags"
                                            placeholder="productivity, creative, technical"
                                            value={tags}
                                            onChange={(e) => setTags(e.target.value)}
                                            disabled={status === 'loading'}
                                            className="text-sm"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Comma-separated keywords for discovery
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                                <AlertCircle className="h-4 w-4 shrink-0" />
                                {error}
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    {status === 'success' ? (
                        <Button onClick={handleClose}>Close</Button>
                    ) : (
                        <>
                            <Button
                                variant="outline"
                                onClick={handleClose}
                                disabled={status === 'loading'}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handlePublish}
                                disabled={status === 'loading'}
                            >
                                {status === 'loading' ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Publishing...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="h-4 w-4 mr-2" />
                                        Publish
                                    </>
                                )}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
